/**
 * SOVRANOVA — COUPLES: solo-guided relationship practice (EFT + Gottman).
 * One partner works on the relationship on their own: pick a guided exercise,
 * reflect, and get warm, structured, non-blaming guidance (Reflect · Insight ·
 * Try · Ask). Wellness/education only — not couples therapy, and safety-first.
 * Gated by VITE_FEATURE_COUPLES (frontend) + FEATURE_COUPLES (backend).
 */
import { useState, useEffect } from 'react'
import { api } from '../../services/api'

export function CouplesView({ onClose, onHome }) {
  const [exercises, setExercises] = useState([])
  const [selected,  setSelected]  = useState(null)   // exercise object
  const [input,     setInput]     = useState('')
  const [guidance,  setGuidance]  = useState(null)   // { reflect, insight, tryThis, ask } | { safety }
  const [busy,      setBusy]      = useState(false)
  const [err,       setErr]       = useState(null)
  const [minor,     setMinor]     = useState(false)
  const [reflections, setReflections] = useState([])   // own completed joint sessions
  const [safetyOpen, setSafetyOpen] = useState(() => {
    try { return localStorage.getItem('sov_couples_safety_v1') !== '1' } catch { return true }
  })
  const dismissSafety = () => { setSafetyOpen(false); try { localStorage.setItem('sov_couples_safety_v1', '1') } catch {} }

  useEffect(() => {
    api.get('/couples/exercises')
      .then(r => setExercises(r?.exercises || []))
      .catch(e => { if (String(e.message) === 'minor_account') setMinor(true) })
  }, [])

  // Your own completed joint sessions. Silent on failure and simply absent when
  // empty — a new user should never see a hollow "no sessions yet" shelf.
  useEffect(() => {
    api.get('/couples/reflections')
      .then(r => setReflections(Array.isArray(r?.reflections) ? r.reflections : []))
      .catch(() => setReflections([]))
  }, [])

  const openExercise = (ex) => { setSelected(ex); setInput(''); setGuidance(null); setErr(null) }
  const backToList   = () => { setSelected(null); setInput(''); setGuidance(null); setErr(null) }

  const submit = async () => {
    if (!input.trim()) return
    setBusy(true); setErr(null); setGuidance(null)
    try {
      const r = await api.post('/couples/guide', { exerciseId: selected.id, input })
      setGuidance(r || null)
    } catch (e) {
      if (String(e.message) === 'minor_account') setMinor(true)
      else setErr("Couldn't reflect on that just now — take a breath and try again in a moment.")
    }
    setBusy(false)
  }

  if (minor) {
    return (
      <div style={s.page}>
        <div style={s.topbar}><button style={s.back} onClick={onClose}>← Back</button></div>
        <div style={s.minorWrap}>
          <div style={{ fontSize:'2rem' }}>🌱</div>
          <h2 style={s.minorTitle}>This space is for grown-ups</h2>
          <p style={s.minorText}>The Couples practice is designed for adults working on a romantic relationship. There's lots else here for you — try Guided Sessions, Journal, or a Meditation.</p>
          <button style={s.primaryBtn} onClick={onClose}>← Back to Journey</button>
        </div>
      </div>
    )
  }

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <button style={s.back} onClick={selected ? backToList : onClose}>← {selected ? 'Exercises' : 'Back'}</button>
        <button style={s.homeBtn} onClick={onHome || onClose} title="Dashboard">⌂ Home</button>
      </div>

      {!selected && (
        <>
          <header style={s.hero}>
            <p style={s.kicker}>SOVRANOVA · COUPLES</p>
            <h1 style={s.h1}>Grow the relationship — starting with you</h1>
            <p style={s.sub}>Short, guided practices drawn from two of the most researched approaches to love: Emotionally Focused Therapy and the Gottman Method. You work on your own; connection follows.</p>
          </header>

          {safetyOpen ? (
            <div style={s.safetyCard}>
              <p style={s.safetyTitle}>A gentle note first</p>
              <p style={s.safetyItem}>This is a wellness practice, not couples therapy, and not a substitute for professional help. It works on your side of things — it won't diagnose your partner or the relationship.</p>
              <p style={s.safetyItem}>If you ever feel unsafe, afraid, or controlled in your relationship, your safety comes first — please reach out to someone you trust or a family-violence support line, and use Urgent help if you're in danger.</p>
              <button style={s.safetyAck} onClick={dismissSafety}>I understand</button>
            </div>
          ) : (
            <button style={s.safetyReopen} onClick={() => setSafetyOpen(true)}>&#9432; About this space &amp; safety</button>
          )}

          <div style={s.grid}>
            {exercises.map(ex => (
              <button key={ex.id} style={s.card} onClick={() => openExercise(ex)}>
                <span style={s.cardEmoji}>{ex.emoji}</span>
                <span style={s.cardText}>
                  <span style={s.cardName}>{ex.name}</span>
                  <span style={s.cardTag}>{ex.tag}</span>
                </span>
                <span style={s.cardFw}>{ex.framework}</span>
              </button>
            ))}
            {exercises.length === 0 && <p style={s.loading}>Loading practices…</p>}
          </div>

          {/* Joint sessions you've already had, to revisit on your own.
              Server-scoped to YOUR OWN sessions and to the summary written in
              that session — never a partner's individual material. Absent
              entirely until there is at least one closed, summarised session. */}
          {reflections.length > 0 && (
            <section style={s.reflWrap}>
              <h2 style={s.reflHead}>Your joint sessions</h2>
              <p style={s.reflSub}>Summaries from sessions you and your partner did together — here so you can sit with them on your own afterwards.</p>
              {reflections.map(r => (
                <article key={r.sessionId} style={s.reflCard}>
                  <p style={s.reflDate}>
                    {new Date(r.startedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {r.exchanges ? ` · ${r.exchanges} exchanges` : ''}
                  </p>
                  <p style={s.reflBody}>{r.summary}</p>
                </article>
              ))}
            </section>
          )}
        </>
      )}

      {selected && (
        <>
          <header style={s.hero}>
            <p style={s.kicker}>{selected.emoji} {selected.name} · {selected.framework}</p>
            <p style={s.exIntro}>{selected.intro}</p>
          </header>

          <div style={s.promptCard}>
            <p style={s.promptText}>{selected.prompt}</p>
          </div>

          <textarea style={s.input} rows={5} placeholder="Take your time. Write as honestly as you can — this is just for you."
            value={input} onChange={e => setInput(e.target.value)} />
          <button style={s.primaryBtn} onClick={submit} disabled={busy || !input.trim()}>
            {busy ? 'Reflecting with you…' : 'Reflect with me'}
          </button>
          {err && <p style={s.err}>{err}</p>}

          {guidance && guidance.safety && (
            <div style={s.safetyResult}>
              <p style={s.safetyResultTitle}>💗 Your wellbeing comes first</p>
              <p style={s.safetyResultText}>{guidance.safety}</p>
            </div>
          )}

          {guidance && !guidance.safety && (
            <div style={s.result}>
              {guidance.reflect && (<div style={s.block}><p style={s.blockLabel}>Reflecting back</p><p style={s.blockText}>{guidance.reflect}</p></div>)}
              {guidance.insight && (<div style={s.block}><p style={s.blockLabel}>What's underneath</p><p style={s.blockText}>{guidance.insight}</p></div>)}
              {guidance.tryThis && (<div style={{ ...s.block, ...s.tryBlock }}><p style={s.blockLabel}>✨ Try this</p><p style={s.blockText}>{guidance.tryThis}</p></div>)}
              {guidance.ask && (<div style={s.block}><p style={s.blockLabel}>Sit with this</p><p style={{ ...s.blockText, fontStyle:'italic' }}>{guidance.ask}</p></div>)}
            </div>
          )}
        </>
      )}

      <p style={s.foot}>What you write here is private to you. Real change is small and repeated — one honest reflection at a time.</p>
    </div>
  )
}

const s = {
  page:    { minHeight:'100vh', background:'linear-gradient(170deg,#241019,#1b0c14)', padding:'1.2rem 1.1rem 2.5rem', display:'flex', flexDirection:'column', gap:'1rem', color:'#f4e8ee' },
  topbar:  { display:'flex', alignItems:'center', justifyContent:'space-between' },
  back:    { background:'transparent', border:'none', color:'rgba(244,232,238,.6)', fontSize:'0.9rem', cursor:'pointer' },
  homeBtn: { background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.15)', color:'rgba(255,255,255,.65)', borderRadius:'0.5rem', padding:'0.3rem 0.65rem', fontSize:'0.78rem', cursor:'pointer' },
  hero:    { display:'flex', flexDirection:'column', gap:'0.4rem' },

  // Joint-session reflections. Quieter than the exercise cards on purpose:
  // this is a shelf you come back to, not a call to action.
  reflWrap: { display:'flex', flexDirection:'column', gap:'0.6rem', marginTop:'1.6rem', paddingTop:'1.2rem', borderTop:'1px solid rgba(255,255,255,.09)' },
  reflHead: { fontSize:'1.05rem', fontWeight:700, margin:0 },
  reflSub:  { fontSize:'0.8rem', color:'rgba(244,232,238,.5)', lineHeight:1.55, margin:'0 0 0.3rem' },
  reflCard: { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.09)', borderRadius:'0.9rem', padding:'0.9rem 1rem' },
  reflDate: { fontSize:'0.68rem', letterSpacing:'0.08em', textTransform:'uppercase', color:'rgba(244,232,238,.42)', margin:'0 0 0.45rem' },
  reflBody: { fontSize:'0.88rem', lineHeight:1.65, color:'rgba(244,232,238,.85)', margin:0, whiteSpace:'pre-wrap' },
  kicker:  { fontSize:'0.66rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'#e08aa6', fontWeight:700, margin:0 },
  h1:      { fontSize:'1.7rem', fontWeight:800, color:'#fbeff4', margin:0, lineHeight:1.2 },
  sub:     { fontSize:'0.9rem', color:'rgba(244,232,238,.6)', lineHeight:1.55, margin:0 },
  exIntro: { fontSize:'0.92rem', color:'rgba(244,232,238,.72)', lineHeight:1.6, margin:'0.3rem 0 0' },
  safetyCard:  { background:'rgba(255,255,255,.035)', border:'1px solid rgba(224,138,166,.28)', borderRadius:'1rem', padding:'1rem 1.1rem', display:'flex', flexDirection:'column', gap:'0.5rem' },
  safetyTitle: { fontSize:'0.8rem', fontWeight:700, color:'#e08aa6', letterSpacing:'0.03em', margin:0 },
  safetyItem:  { fontSize:'0.8rem', color:'rgba(244,232,238,.62)', lineHeight:1.55, margin:0 },
  safetyAck:   { alignSelf:'flex-start', marginTop:'0.3rem', background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.15)', color:'#fbeff4', borderRadius:'0.6rem', padding:'0.45rem 1rem', fontSize:'0.8rem', cursor:'pointer' },
  safetyReopen:{ alignSelf:'flex-start', background:'transparent', border:'none', color:'rgba(224,138,166,.75)', fontSize:'0.76rem', cursor:'pointer', padding:0 },
  grid:    { display:'flex', flexDirection:'column', gap:'0.6rem' },
  card:    { display:'flex', alignItems:'center', gap:'0.85rem', width:'100%', textAlign:'left', background:'rgba(255,255,255,.045)', border:'1px solid rgba(255,255,255,.09)', borderRadius:'1rem', padding:'0.95rem 1.05rem', cursor:'pointer', color:'#f4e8ee' },
  cardEmoji:{ fontSize:'1.5rem', flexShrink:0 },
  cardText: { display:'flex', flexDirection:'column', flex:1, minWidth:0, gap:'0.15rem' },
  cardName: { fontSize:'0.98rem', fontWeight:700, color:'#fbeff4' },
  cardTag:  { fontSize:'0.76rem', color:'rgba(244,232,238,.5)' },
  cardFw:   { fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.06em', color:'rgba(224,138,166,.85)', background:'rgba(224,138,166,.12)', borderRadius:'0.5rem', padding:'0.2rem 0.45rem', flexShrink:0 },
  loading:  { fontSize:'0.85rem', color:'rgba(244,232,238,.4)', textAlign:'center', padding:'1rem' },
  promptCard:{ background:'rgba(224,138,166,.08)', border:'1px solid rgba(224,138,166,.22)', borderRadius:'0.9rem', padding:'0.95rem 1.05rem' },
  promptText:{ fontSize:'0.95rem', color:'#fbeff4', lineHeight:1.55, margin:0, fontWeight:500 },
  input:   { width:'100%', boxSizing:'border-box', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.14)', borderRadius:'0.8rem', padding:'0.9rem', color:'#fbeff4', fontSize:'1rem', fontFamily:'inherit', resize:'vertical', lineHeight:1.5 },
  primaryBtn:{ padding:'0.95rem 1rem', width:'100%', background:'linear-gradient(135deg,#b5476c,#e0728f)', color:'#fff', border:'none', borderRadius:'0.85rem', fontSize:'1rem', fontWeight:700, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 4px 18px rgba(224,114,143,.28)' },
  err:     { fontSize:'0.82rem', color:'#f0a', margin:0 },
  result:  { display:'flex', flexDirection:'column', gap:'0.7rem', marginTop:'0.2rem' },
  block:   { background:'rgba(255,255,255,.045)', border:'1px solid rgba(255,255,255,.09)', borderRadius:'0.85rem', padding:'0.85rem 1rem' },
  tryBlock:{ background:'rgba(224,138,166,.1)', border:'1px solid rgba(224,138,166,.3)' },
  blockLabel:{ fontSize:'0.66rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(224,138,166,.9)', fontWeight:700, margin:'0 0 0.3rem' },
  blockText:{ fontSize:'0.92rem', color:'#f4e8ee', lineHeight:1.6, margin:0 },
  safetyResult:{ background:'rgba(224,138,166,.12)', border:'1px solid rgba(224,138,166,.4)', borderRadius:'0.9rem', padding:'1rem 1.1rem', marginTop:'0.2rem' },
  safetyResultTitle:{ fontSize:'0.9rem', fontWeight:700, color:'#f6c2d2', margin:'0 0 0.4rem' },
  safetyResultText:{ fontSize:'0.9rem', color:'#f4e8ee', lineHeight:1.65, margin:0, whiteSpace:'pre-line' },
  minorWrap:{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'0.8rem', textAlign:'center', padding:'2rem 1rem' },
  minorTitle:{ fontSize:'1.3rem', fontWeight:800, color:'#fbeff4', margin:0 },
  minorText:{ fontSize:'0.9rem', color:'rgba(244,232,238,.6)', lineHeight:1.6, maxWidth:340, margin:0 },
  foot:    { fontSize:'0.76rem', color:'rgba(244,232,238,.4)', lineHeight:1.6, fontStyle:'italic', borderTop:'1px solid rgba(255,255,255,.08)', paddingTop:'0.9rem', margin:'0.5rem 0 0' },
}
